import { type PluginManifest } from "@manifold/protocol";

/**
 * `core.commands` — the workspace's own doors, keys and rooms, in one searchable surface
 * (issue #129).
 *
 * IT DECLARES NO VOCABULARY OF ITS OWN, and that is the whole roster-restraint argument for a
 * new seat rather than a new feature. Everything this plugin shows is a registry the engine
 * already composed and already publishes to every plugin alike (`host.assembly`):
 *
 *   DOORS       every composed action, with the caps it costs — `roster().actions`
 *   KEYS        every composed binding, with the keystroke it answers to — `assembly.bindings`
 *   ROOMS       every container in the workspace index — `client.index()`
 *
 * So it is not an opinionated feature in the box: it is the universal door-opener, and its
 * data model is the composition itself. Add a plugin and its doors and keys appear here for
 * free; disable one and they leave, because composition dropped them upstream. A palette that
 * kept its own list of commands would be exactly the second roster docs/CONTRACTS.md §One authoritative implementation forbids —
 * this one cannot have one, because it declares nothing.
 *
 * WHAT IT CONTRIBUTES is one binding and one workspace overlay, and no seat in the rail. A
 * command menu is reached by its key from wherever the viewer is standing — including the
 * workspace root, where no container is mounted — which is what the workspace overlay channel
 * is for. A rail row would be a second door onto one surface (docs/CONTRACTS.md §One authoritative implementation) and would need a
 * mark the engine's closed control vocabulary has no neutral verb for; the key table lists
 * this plugin's row like every other, which is where a reader learns the keystroke.
 *
 * A door with no published fields runs immediately. A door with fields opens the shared
 * `@manifold/plugin/ui` generated form, which renders the action's own schema and submits the
 * reader's validated value through the same action client. The list never guesses arguments or
 * interprets the schema itself; that last rung remains the door's answer.
 *
 * `cmdk` is the list behind it, pinned and boring
 * (`docs/decisions/2026-09-01-cmdk-command-menu.md`).
 */
export const commandsManifest: PluginManifest = {
  id: "core.commands",
  version: "1.0.0",
  title: "Commands",
  description:
    "One searchable surface over the composition: every door with the authority it costs, every key with the stroke it answers to, and every container in the index.",
  capabilities: [],
  contributes: {
    panels: [],
    sections: [],
    elements: [],
    tools: [],
    events: [],
  },
};

/**
 * The binding id, built from the manifest id rather than spelled, exactly as an action name is:
 * the row, the key table and this package's own registration cannot drift from the declaration.
 */
export const COMMANDS_OPEN_BINDING = `${commandsManifest.id}.open`;
